<template>
  <v-card class="col-picker-card">
    <div class="col-picker-header pa-3 d-flex align-center">
      <font-awesome-icon
        :icon="['fas', 'table-columns']"
        size="sm"
        class="mr-2"
      />
      <span class="subtitle-2 font-weight-medium">Columns</span>

      <v-spacer />

      <font-awesome-icon
        :icon="['fas', 'info-circle']"
        size="sm"
        class="grey--text cursor-pointer"
        @click.stop="showHelpForColumns = !showHelpForColumns"
      />
    </div>

    <v-divider v-if="showHelpForColumns" />

    <v-row v-if="showHelpForColumns">
      <v-col>
        <div class="mt-4 mx-4 caption grey--text">
          <font-awesome-icon
            :icon="['fas', 'circle-info']"
            class="mr-1"
          />
          Reorder, freeze, and toggle the visibility of columns.
        </div>
      </v-col>
    </v-row>

    <div class="col-picker-content pa-4 col-picker-columns">
      <div class="caption mb-3 grey--text">
        <font-awesome-icon
          :icon="['fas', 'grip-dots-vertical']"
          class="mr-1"
        />
        Drag to reorder
        <span class="mx-2">|</span>
        <font-awesome-icon
          :icon="['fas', 'thumbtack']"
          class="mr-1"
        />
        Pin to freeze
        <span class="mx-2">|</span>
        <font-awesome-icon
          :icon="['fas', 'square-check']"
          class="mr-1"
        />
        Toggle visibility
      </div>

      <v-list
        dense
        class="col-picker-list"
      >
        <draggable v-model="orderedColumns">
          <v-list-item
            v-for="(column, colIndex) of orderedColumns"
            :key="column.key || colIndex"
            dense
            class="col-picker-item"
          >
            <v-list-item-icon class="mr-2 my-auto">
              <font-awesome-icon
                :icon="['fas', 'grip-dots-vertical']"
                class="cursor-grab grey--text"
              />
            </v-list-item-icon>

            <v-list-item-icon class="mr-2 my-auto">
              <font-awesome-icon
                :icon="['fas', 'thumbtack']"
                :class="`cursor-pointer ${column.frozen ? 'primary--text' : 'grey--text'}`"
                :title="`${column.frozen ? 'Unpin' : 'Pin'} ${getColumnLabel(column)}`"
                @click="freezeColumn(column)"
              />
            </v-list-item-icon>

            <v-list-item-icon class="mr-2 my-auto">
              <font-awesome-icon
                v-if="column.visible || column.required"
                :icon="['fas', 'square-check']"
                :class="`cursor-pointer ${column.visible && !column.required ? 'primary--text' : 'grey--text'}`"
                :title="`${column.visible && !column.required ? 'Hide' : 'Show'} ${getColumnLabel(column)}`"
                @click="toggleColumn(column)"
              />
              <font-awesome-icon
                v-else
                :icon="['far', 'square']"
                class="cursor-pointer grey--text"
                :title="`Show ${getColumnLabel(column)}`"
                @click="toggleColumn(column)"
              />
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title class="body-2">
                {{ getColumnLabel(column) }}
                <span
                  v-if="column.required"
                  class="caption grey--text ml-1"
                >(required)</span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </draggable>
      </v-list>
    </div>
  </v-card>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import draggable from 'vuedraggable'
import columnUtils from '@/tools/store/utils/columns'

const { mapGetters: mapToolsGetters } = createNamespacedHelpers('tools')

export default {
  name: 'ColumnPicker',
  components: { draggable },
  props: {
    currentRequiredColumns: {
      type: Array,
      default: () => [],
    },
    reportKey: {
      type: String,
      default: undefined,
    },
    tableColumns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showHelpForColumns: false,
    }
  },
  computed: {
    ...mapToolsGetters(['frozenColumns']),
    orderedColumns: {
      get() {
        return columnUtils.normalizeColumns(this.tableColumns, this.currentRequiredColumns)
      },
      set(value) {
        this.$store.commit('tools/columns', { [this.reportKey]: value })
      },
    },
  },
  methods: {
    getColumnLabel(column) {
      return columnUtils.getColumnLabel(column)
    },
    getColumnIdentifier(column) {
      return columnUtils.getColumnKey(column)
    },
    findColumnIndex(column) {
      const identifier = this.getColumnIdentifier(column)

      if (!identifier) return -1

      return (this.tableColumns || []).findIndex(
        (currentColumn) => this.getColumnIdentifier(currentColumn) === identifier,
      )
    },
    /**
     * @method freezeColumn
     * @desc Freezes the specified column in the table.
     * @param column {Object}
     * @return {void}
     * @see src/tools/store/actions.js#freezeColumn
     */
    freezeColumn(column) {
      const { reportKey } = this
      const columnIndex = this.findColumnIndex(column)
      const tableColumns = this.orderedColumns

      // Dispatch an action to freeze the column in the store.
      this.$store.dispatch('tools/freezeColumn', { columnIndex, reportKey, tableColumns })
    },
    toggleColumn(column) {
      const { currentRequiredColumns, tableColumns, reportKey } = this
      const columnIndex = this.findColumnIndex(column)

      if (columnIndex === -1) {
        return false
      }

      const newColumns = [...tableColumns]
      const currentColumn = newColumns[columnIndex]
      const currentColumnIdentifier = this.getColumnIdentifier(currentColumn)
      const isRequiredColumn = currentRequiredColumns?.length
        && currentRequiredColumns.indexOf(currentColumnIdentifier) !== -1

      if (isRequiredColumn) {
        const message = 'Cannot Hide Required Column'

        this.$store.commit('tools/snackbar', true)
        this.$store.commit('tools/snack', { message, status: 'error' })

        return false
      }

      newColumns[columnIndex] = {
        ...currentColumn,
        visible: currentColumn.visible === false,
      }

      this.$store.commit('tools/columns', { [reportKey]: newColumns })

      return newColumns
    },
  },
}
</script>

<style scoped lang="scss">
.col-picker-card {
  max-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
}

.col-picker-header {
  flex-shrink: 0;
  background-color: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.theme--dark .col-picker-header {
  background-color: #2d2d2d;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.col-picker-content {
  overflow-y: auto;
  flex: 1 1 auto;
  max-height: calc(100vh - 340px);
}

.col-picker-columns {
  .col-picker-list {
    max-height: calc(100vh - 380px);
    overflow-y: auto;
  }

  .col-picker-item {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
}

.theme--dark .col-picker-columns {
  .col-picker-item {
    border-bottom-color: rgba(255, 255, 255, 0.06);
  }
}

.cursor-grab {
  cursor: grab;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
